import * as React from 'react';
import { Alert } from 'lib/alert/models';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import { capitalize } from 'helpers/helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteAlert } from 'lib/alert/hooks/useDeleteAlert';
import { useDispatch, useSelector } from 'store/hooks';
import { useState } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { NotificationsOff } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import { useToggleAlertMute } from 'lib/alert/hooks/useToggleAlertMute';
import { HttpError } from 'store/APIClient';
import IconMenu from '../IconMenu';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import MuteAlertButton from './MuteAlertButton';
import { useAlertAction } from './Contexts/AlertActionContext';

type Props = {
    alert: Alert;
    chartKey: number;
    isSubscribedAlert: boolean;
};

type AlertAction = {
    icon: JSX.Element;
    label: string;
    action: () => void;
    hidden?: boolean;
};

const AlertActions = ({
    alert,
    chartKey,
    isSubscribedAlert,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const user = useSelector((state) => state.vision.user);
    const dispatch = useDispatch();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: deleteAlert } = useDeleteAlert('chart', chartKey, alert.id);

    const { setAction } = useAlertAction();

    const isRecipient = alert.recipients.some(
        (recipient) => recipient.email === user.email,
    );

    const onSuccess = (message: string) => {
        queryClient.setQueryData(
            ['chart', chartKey, 'alerts', 'owned'],
            (oldAlerts: Array<Alert>) =>
                oldAlerts.map((oldAlert) => {
                    if (oldAlert.id === alert.id) {
                        return {
                            ...oldAlert,
                            isMuted: !oldAlert.isMuted,
                        };
                    }
                    return oldAlert;
                }),
        );
        dispatch.vision.notifyUser({
            message,
            snackType: 'success',
        });
    };

    const onError = (error: HttpError) => {
        dispatch.vision.notifyUser({
            message: error.message,
            snackType: 'error',
        });
    };

    const { mutate: toggleMuted } = useToggleAlertMute(
        alert.id,
        onSuccess,
        onError,
    );

    const editAlert = () => setAction({ name: 'edit', alertId: alert.id });

    const menuActions: ReadonlyArray<AlertAction> = [
        {
            icon: <EditIcon />,
            label: t('alert.edit.label'),
            action: editAlert,
        },
        {
            icon: <DeleteIcon />,
            label: capitalize(t('alert.delete.title')),
            action: () => setShowConfirmDelete(true),
        },
        {
            icon: alert.isMuted ? <NotificationsIcon /> : <NotificationsOff />,
            label: alert.isMuted
                ? t('alert.muted.disable')
                : t('alert.muted.enable'),
            action: () => toggleMuted(!alert.isMuted),
            hidden: !isRecipient,
        },
    ];

    const availableMenuActions = menuActions.filter(
        (menuAction) => !menuAction.hidden,
    );

    return (
        <div style={{ marginRight: '1rem' }}>
            {availableMenuActions.length > 0 && !isSubscribedAlert && (
                <IconMenu
                    iconElement={<MoreVertIcon fontSize="small" />}
                    tooltip={capitalize(t('common.options'))}
                    tooltipPlacement="bottom"
                >
                    {availableMenuActions.map((menuAction) => (
                        <MenuItem
                            key={menuAction.label}
                            onClick={menuAction.action}
                        >
                            <ListItemIcon>{menuAction.icon}</ListItemIcon>
                            {menuAction.label}
                        </MenuItem>
                    ))}
                </IconMenu>
            )}
            {isSubscribedAlert && (
                <MuteAlertButton alert={alert} chartKey={chartKey} />
            )}
            <ConfirmDeleteModal
                show={showConfirmDelete}
                cancel={() => setShowConfirmDelete(false)}
                confirm={() => deleteAlert()}
                title={t('alert.delete.confirmTitle', {
                    name: alert.name,
                })}
                secondary={t('alert.delete.secondary')}
            />
        </div>
    );
};

export default AlertActions;
